/**
 * Shared Dialog chrome for Create/Refine and Edit job modals —
 * same shell height, scroll body, and pinned inset footer.
 */

import React from 'react';

import { Button } from '@/components/ui/atoms';
import { Dialog } from '@/components/ui/organisms/Dialog';
import { cn } from '@/utils/cn';

import { ModalStepper } from './ModalStepper';

/** Applied to Dialog `className` (shell) — ! overrides Dialog defaults (max-h / margin). */
export const jobFlowDialogClassName = cn(
  'overflow-hidden !my-0 sm:!my-4 !rounded-none sm:!rounded-xl',
  // Mobile: hug content, viewport-capped (vh then dvh)
  'h-auto !max-h-[100vh] !max-h-[100dvh]',
  // md+: fixed shell (~tallest step)
  'md:!h-[42rem] md:!max-h-[calc(100vh-4rem)] md:!max-h-[calc(100dvh-4rem)]'
);

/** Applied to Dialog `contentClassName` — Header / Body / Footer own spacing. */
export const jobFlowDialogContentClassName = '!p-0 !gap-0 !pb-0';

/** Applied to Dialog.Body. */
export const jobFlowDialogBodyClassName =
  'flex flex-col min-h-0 overscroll-contain px-6 pt-1 pb-5 [scrollbar-gutter:stable]';

/** Applied to Dialog.Footer. */
export const jobFlowDialogFooterClassName = cn(
  'w-full !mt-0 !justify-between',
  'relative shrink-0 z-10 bg-white',
  'px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]',
  "before:pointer-events-none before:absolute before:inset-x-6 before:top-0 before:border-t before:border-gray-200 before:content-['']"
);

export interface JobFlowDialogChromeProps {
  title: string;
  closeAriaLabel: string;
  showCloseButton?: boolean;
  closeDisabled?: boolean;
  stepperSteps: Array<{ key: string; label: string }>;
  stepperCurrent: string;
}

/** Shared Dialog.Header + ModalStepper block for Create and Edit shells. */
export const JobFlowDialogChrome: React.FC<JobFlowDialogChromeProps> = ({
  title,
  closeAriaLabel,
  showCloseButton = true,
  closeDisabled = false,
  stepperSteps,
  stepperCurrent,
}) => (
  <>
    <div className="shrink-0 px-6 pt-6 pb-4">
      <Dialog.Header
        title={title}
        titleVariant="h5"
        showCloseButton={showCloseButton}
        closeButtonProps={{
          'aria-label': closeAriaLabel,
          disabled: closeDisabled,
        }}
      />
    </div>
    <div className="shrink-0 px-6 pb-4">
      <ModalStepper steps={stepperSteps} currentStep={stepperCurrent} />
    </div>
  </>
);

export interface JobFlowModalFooterActionsProps {
  backLabel: string;
  primaryLabel: string;
  onBack: () => void;
  onPrimary: () => void;
  backDisabled?: boolean;
  primaryDisabled?: boolean;
}

/** Back + primary actions for Dialog.Footer (shared Edit / Refine). */
export const JobFlowModalFooterActions: React.FC<JobFlowModalFooterActionsProps> = ({
  backLabel,
  primaryLabel,
  onBack,
  onPrimary,
  backDisabled = false,
  primaryDisabled = false,
}) => (
  <>
    <Button
      label={backLabel}
      onClick={onBack}
      type="outlined"
      intent="secondary"
      disabled={backDisabled}
      className="w-full sm:w-auto"
    />
    <Button
      label={primaryLabel}
      onClick={onPrimary}
      type="contained"
      intent="primary"
      disabled={primaryDisabled}
      className="w-full sm:w-auto"
    />
  </>
);
